// File: utils/annotationSync.js

/**
 * Keeps bookmarks and notes in sync with the signed-in CDRisk account.
 *
 * Signed out: everything stays in localStorage exactly as before.
 * On sign-in: local state is bulk-merged with the server (newer side wins
 * per section) and the merged result is written back locally.
 * While signed in: storage.js dispatches 'osha:annotation-changed' after
 * every bookmark/note mutation; we debounce and push that section.
 */

import { apiFetch, isSignedIn } from './account.js';
import {
  getBookmarks,
  setBookmarks,
  getNotes,
  setNotes,
} from './storage.js';

const pendingPush = new Map(); // sectionCode -> timeout id

function localAnnotations() {
  const bookmarks = getBookmarks();
  const notes = getNotes();
  const byCode = new Map();

  for (const bookmark of bookmarks) {
    byCode.set(bookmark.code, {
      sectionCode: bookmark.code,
      bookmarked: true,
      note: '',
      updatedAt: bookmark.bookmarked_at || new Date(0).toISOString(),
    });
  }
  for (const note of notes) {
    const existing = byCode.get(note.section);
    if (existing) {
      existing.note = note.text;
      if (note.updated_at > existing.updatedAt) existing.updatedAt = note.updated_at;
    } else {
      byCode.set(note.section, {
        sectionCode: note.section,
        bookmarked: false,
        note: note.text,
        updatedAt: note.updated_at || new Date(0).toISOString(),
      });
    }
  }
  return Array.from(byCode.values());
}

/** Mirror the server's merged annotation set into localStorage. */
function applyMerged(annotations) {
  const localBookmarks = getBookmarks();
  const titleByCode = new Map(localBookmarks.map((b) => [b.code, b]));

  const bookmarks = [];
  const notes = [];
  for (const ann of annotations) {
    if (ann.bookmarked) {
      const local = titleByCode.get(ann.sectionCode);
      bookmarks.push(
        local || { code: ann.sectionCode, title: ann.sectionCode, bookmarked_at: ann.updatedAt }
      );
    }
    if (ann.note) {
      notes.push({
        section: ann.sectionCode,
        text: ann.note,
        created_at: ann.updatedAt,
        updated_at: ann.updatedAt,
      });
    }
  }
  setBookmarks(bookmarks);
  setNotes(notes);
}

/** Full two-way merge. Call after sign-in and on app load when signed in. */
export async function fullSync() {
  if (!isSignedIn()) return false;
  try {
    const data = await apiFetch('/api/regs/annotations/sync', {
      method: 'POST',
      auth: true,
      body: { items: localAnnotations() },
    });
    if (Array.isArray(data.annotations)) {
      applyMerged(data.annotations);
      window.dispatchEvent(new CustomEvent('osha:annotations-synced'));
    }
    return true;
  } catch (error) {
    console.warn('Annotation sync failed:', error.message);
    return false;
  }
}

function pushSection(sectionCode) {
  if (!isSignedIn()) return;
  const bookmarked = getBookmarks().some((b) => b.code === sectionCode);
  const note = getNotes().find((n) => n.section === sectionCode);
  apiFetch(`/api/regs/annotations/${encodeURIComponent(sectionCode)}`, {
    method: 'PUT',
    auth: true,
    body: { note: note ? note.text : '', bookmarked },
  }).catch((error) => console.warn('Annotation push failed:', error.message));
}

/** Wire up live push + initial sync. Call once at app start. */
export function initAnnotationSync() {
  window.addEventListener('osha:annotation-changed', (event) => {
    const code = event.detail?.code;
    if (!code || !isSignedIn()) return;
    // Debounce per section — typing in the note editor saves repeatedly
    clearTimeout(pendingPush.get(code));
    pendingPush.set(
      code,
      setTimeout(() => {
        pendingPush.delete(code);
        pushSection(code);
      }, 1500)
    );
  });

  window.addEventListener('cdrisk:account-changed', () => {
    if (isSignedIn()) fullSync();
  });

  if (isSignedIn()) fullSync();
}
